// src/modals/BlockEditDatesModal.jsx — Lot 2 UI (ADR-258, #883)
//
// Modale d'édition des DATES d'un blocage FBZ simple (pricing_block sans
// voyageur ni argent). Permet de RACCOURCIR ou ÉTENDRE la période, puis pousse
// le différentiel vers Beds24 côté serveur (action pricing_block_edit_dates) :
//   - RACCOURCISSEMENT → réouverture (numAvail=1) des nuits libérées =
//     CHEMIN ARGENT (on remet des nuits en vente sur Airbnb/Booking). Récap
//     ROUGE + confirmation en 2 étapes, sur le modèle EXACT du mode « rouvrir »
//     de Beds24DivergenceModal (jamais un window.confirm/alert).
//   - EXTENSION → fermeture (numAvail=0) des nuits ajoutées.
//
// Le récap « N nuits rouvertes / fermées » est calculé côté client avec le
// helper PUR partagé `computeDatesDiff` (src/lib/pricing-block.js) sur les dates
// AFFICHÉES à l'ouverture de la modale (expectedStart/expectedEnd). Ces mêmes
// dates sont renvoyées au serveur en `expected_start`/`expected_end` (verrou
// optimiste) : si le bloc a bougé entre-temps, le serveur refuse (409
// dates_changed_concurrently) AVANT tout push → le récap ne peut pas mentir.
//
// LECTURE-ONLY côté client : aucune décision métier, aucun push Beds24 ici —
// l'auth (requireAdmin) et le push réel restent au serveur. Ce composant
// orchestre l'appel + rend les retours lisibles.

import { useEffect, useMemo, useRef, useState } from "react";
import { AlertTriangle, CalendarClock, Check, Loader2 } from "lucide-react";
import { DS } from "../lib/ui";
import { supabase } from "../supabase";
import {
  computeDatesDiff,
  daysBetween,
  isValidISODate,
} from "../lib/pricing-block";

const MOIS_FR = [
  "janvier",
  "février",
  "mars",
  "avril",
  "mai",
  "juin",
  "juillet",
  "août",
  "septembre",
  "octobre",
  "novembre",
  "décembre",
];

// Formate une date ISO (YYYY-MM-DD) en français sans dépendre du fuseau
// (parse manuel, pas de new Date(iso) qui décale à minuit UTC).
function formatDateFR(iso) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso || "");
  if (!m) return iso || "";
  const [, y, mo, d] = m;
  return `${parseInt(d, 10)} ${MOIS_FR[parseInt(mo, 10) - 1]} ${y}`;
}

// Aujourd'hui (fuseau Paris) en ISO — pour bloquer la saisie de dates passées.
function todayParisISO() {
  const p = new Date(
    new Date().toLocaleString("en-US", { timeZone: "Europe/Paris" }),
  );
  return `${p.getFullYear()}-${String(p.getMonth() + 1).padStart(2, "0")}-${String(p.getDate()).padStart(2, "0")}`;
}

// Nb de nuits couvertes par une liste de zones [from, toExclusive] (Convention A).
function nightsInZones(zones) {
  return (zones || []).reduce(
    (acc, [from, to]) => acc + daysBetween(from, to),
    0,
  );
}

// Décrit les dates réelles d'une liste de zones [from, toExcl], en nuits.
// Ex : [["2026-07-07","2026-07-08"]] → « nuit du 7 juillet 2026 ».
function describeZones(zones) {
  return (zones || [])
    .map(([from, to]) => {
      const n = daysBetween(from, to);
      const lastNight = daysBetween(from, to) > 1 ? formatDateFR(to) : null;
      if (n <= 1) return `nuit du ${formatDateFR(from)}`;
      // to est exclusif (jour de départ) → dernière nuit = to - 1 ; on cite la
      // 1re nuit et le jour de départ pour rester lisible.
      return `${n} nuits du ${formatDateFR(from)} au ${lastNight} (départ)`;
    })
    .join(" ; ");
}

// Traduit un couple (status, error) serveur en message FR lisible. Aucun code
// anglais brut ne doit fuir à l'écran (QA pessimist).
function errorMessageFor(status, json) {
  const code = json?.error || null;
  switch (code) {
    case "dates_changed_concurrently":
      return "Le blocage a changé entre-temps — fermez puis rouvrez la fiche, vérifiez les dates et réessayez.";
    case "disjoint_relocation_forbidden":
      return "Déplacer un blocage n'est pas permis. Pour le déplacer, supprimez-le puis recréez-le (libérer + créer).";
    case "direct_block_not_editable":
      return "Ce blocage porte une réservation directe (voyageur / paiement / caution) — ses dates ne sont pas modifiables ici.";
    case "cannot_edit_started_block_start":
      return "Ce blocage a déjà commencé : sa date de début ne peut plus changer (seule la date de fin est modifiable).";
    case "reservation_overlap":
    case "overlap_reservation":
      return "Une réservation existe sur les nuits concernées — réouverture refusée.";
    case "booking_exists_on_beds24":
      return "Une réservation active existe côté Beds24 sur ces nuits — réouverture refusée.";
    case "block_overlap":
    case "overlap_block":
      return "Ces dates chevauchent un autre blocage.";
    case "missing_room_id":
      return "Logement sans chambre Beds24 configurée — réouverture impossible.";
    // Lot L2 (P-3.37, 30/10) — Beds24 en sommeil : la phrase du serveur fait foi.
    case "beds24_en_sommeil":
      return (
        json?.message ||
        "Beds24 est en sommeil : les dates de ce blocage ne se modifient plus depuis l'appli. Rien n'a été modifié."
      );
    case "beds24_check_failed":
      return "Vérification Beds24 impossible — réessayez dans un instant.";
    case "logement_not_configured":
    case "cfg_select_failed":
      return "Logement non configuré pour Beds24.";
    case "block_not_found":
      return "Ce blocage n'existe plus — rechargez la fiche.";
    case "confirm_required":
      return "Confirmation requise pour rouvrir des nuits à la vente.";
    case "horizon_too_large":
      return "Dates trop lointaines (au-delà de l'horizon Beds24).";
    case "start_date_past":
      return "La date de début est dans le passé.";
    // Codes de validation « ne devrait pas arriver » depuis cette UI.
    case "block_id_invalid":
    case "dates_invalid":
    case "date_range_inverted":
    case "start_date_invalid":
    case "end_date_invalid":
    case "reason_invalid_or_too_long":
      return "Requête invalide — rechargez la fiche et réessayez.";
    // Réouverture refusée par Beds24. Le serveur NOMME les nuits (et, en
    // désynchronisation, dit qu'une alerte est partie) : sa phrase fait foi,
    // on ne la réécrit pas ici avec moins d'information.
    case "reopen_push_failed":
    case "reopen_push_failed_desync":
      // ⚠️ PAS « rien n'a été modifié » en repli : le retour se fait PAR BORD,
      // et un bord déjà remis en vente est CONSERVÉ — le blocage peut donc
      // rester sur d'autres dates que celles d'avant. Le serveur le dit dans
      // `message` ; sans lui, on ne promet rien qu'on ne sache.
      return (
        json?.message ||
        "Ces nuits n'ont pas pu être remises en vente sur Beds24. Les dates du " +
          "blocage ont pu changer en partie — rechargez la fiche pour voir son état réel."
      );
    case "rpc_failed":
    case "rpc_unknown_status":
    case "block_select_failed":
    case "select_resas_failed":
      return "Erreur serveur — réessayez.";
    default:
      if (status === 401 || status === 403)
        return "Session expirée — reconnectez-vous.";
      if (status === 502) return "Vérification Beds24 impossible — réessayez.";
      if (status === 404) return "Logement non configuré pour Beds24.";
      return json?.message || code || `Erreur serveur (${status}).`;
  }
}

// Message de succès selon les zones réellement traitées + drapeaux dryRun/queued
// renvoyés par le serveur (source de vérité : jamais le récap client).
function successMessageFor(json) {
  if (json?.noop) return "Dates inchangées — aucune modification.";
  const reopened = nightsInZones(json?.reopenZones);
  const closed = nightsInZones(json?.closeZones);
  const parts = [];
  if (reopened > 0)
    parts.push(
      `${reopened} nuit${reopened > 1 ? "s" : ""} rouverte${reopened > 1 ? "s" : ""} à la vente`,
    );
  if (closed > 0)
    parts.push(
      `${closed} nuit${closed > 1 ? "s" : ""} fermée${closed > 1 ? "s" : ""} à la vente`,
    );
  const detail = parts.length ? ` (${parts.join(", ")})` : "";
  if (json?.dryRun)
    return `Dates modifiées${detail}. Envoi Beds24 SIMULÉ (mode test) — aucun push réel.`;
  // ⚠️ `queued` ne peut plus venir que d'une FERMETURE : la réouverture
  // n'enfile plus (elle échoue franchement et le blocage revient à ses dates
  // d'avant). Promettre « synchro mise en file » sur un message qui compte des
  // nuits ROUVERTES serait faux — c'était le cas avant 2026-09-21.
  // ⚠️ UN PUSH QUI N'EST NI PASSÉ, NI SIMULÉ, NI EN FILE : RIEN N'EST PARTI.
  // C'est le cas d'un logement sans chambre Beds24 (`missing_room_id`) : le
  // helper refuse avant tout envoi et n'enfile rien. L'écran disait pourtant
  // « Disponibilité Beds24 mise à jour » — un calendrier jamais touché.
  const rienParti = (json?.pushes || []).some(
    (p) => !p?.ok && !p?.dryRun && !p?.queued,
  );
  if (rienParti)
    return `Dates modifiées${detail}. ⚠️ RIEN n'a été envoyé à Beds24 (${
      (json.pushes.find((p) => !p?.ok && !p?.dryRun && !p?.queued) || {}).error ||
      "cause inconnue"
    }) et aucun nouvel essai n'est prévu : le calendrier des plateformes n'est PAS à jour.`;
  if (json?.queued)
    return `Dates modifiées${detail}. La fermeture des nuits sur Beds24 n'est pas encore passée : elle est en file, nouvel essai automatique sous ~15 min.`;
  return `Dates modifiées${detail}. Disponibilité Beds24 mise à jour.`;
}

/**
 * @param {object} props
 * @param {boolean} props.open
 * @param {string} props.blockId — UUID du pricing_block
 * @param {string} props.logementLabel — nom lisible du logement
 * @param {string} props.expectedStart — start_date affiché (inclusif, arrivée)
 * @param {string} props.expectedEnd — end_date affiché (Convention A exclusif)
 * @param {function} props.onClose
 * @param {function} props.onSaved — succès : (result) => void (recharge fiche + calendrier)
 */
export default function BlockEditDatesModal({
  open,
  blockId,
  logementLabel,
  expectedStart,
  expectedEnd,
  onClose,
  onSaved,
}) {
  const [newStart, setNewStart] = useState(expectedStart || "");
  const [newEnd, setNewEnd] = useState(expectedEnd || "");
  const [reason, setReason] = useState("");
  const [phase, setPhase] = useState("edit"); // 'edit' | 'confirmReopen'
  const [submitting, setSubmitting] = useState(false);
  const [serverError, setServerError] = useState(null);
  const [successMsg, setSuccessMsg] = useState(null);
  // Garde anti double-submit SYNCHRONE (le state `submitting` est en closure
  // périmée dans le même tick : 2 clics rapides passeraient la garde d'état).
  const submittingRef = useRef(false);

  // Reset à chaque (ré)ouverture ou changement de bloc / dates d'origine.
  useEffect(() => {
    if (open) {
      setNewStart(expectedStart || "");
      setNewEnd(expectedEnd || "");
      setReason("");
      setPhase("edit");
      setSubmitting(false);
      submittingRef.current = false;
      setServerError(null);
      setSuccessMsg(null);
    }
  }, [open, blockId, expectedStart, expectedEnd]);

  // Esc → fermer (sauf pendant un envoi).
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape" && !submitting) onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, submitting, onClose]);

  const today = todayParisISO();
  // Bloc déjà commencé → début figé (le serveur refuse tout autre new_start).
  const started = isValidISODate(expectedStart) && expectedStart < today;

  const diff = useMemo(() => {
    if (!isValidISODate(newStart) || !isValidISODate(newEnd)) return null;
    if (newEnd <= newStart) return null;
    return computeDatesDiff(expectedStart, expectedEnd, newStart, newEnd);
  }, [expectedStart, expectedEnd, newStart, newEnd]);

  const reopenNights = diff ? nightsInZones(diff.reopenZones) : 0;
  const closeNights = diff ? nightsInZones(diff.closeZones) : 0;
  const isReopen = reopenNights > 0;

  // Validité de saisie côté client (le serveur reste juge in fine).
  const datesInvalid =
    !isValidISODate(newStart) || !isValidISODate(newEnd) || newEnd <= newStart;
  const noChange =
    !datesInvalid && newStart === expectedStart && newEnd === expectedEnd;

  if (!open) return null;

  async function submit() {
    if (submittingRef.current) return; // garde anti double-submit (synchrone)
    submittingRef.current = true;
    setSubmitting(true);
    setServerError(null);
    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (!session?.access_token) {
        setServerError("Session expirée — reconnectez-vous.");
        setSubmitting(false);
        submittingRef.current = false;
        return;
      }
      const body = {
        action: "pricing_block_edit_dates",
        block_id: blockId,
        new_start: newStart,
        new_end: newEnd,
        // Verrou optimiste : dates VUES à l'ouverture de la modale (celles du
        // récap). Le serveur refuse si le bloc a bougé depuis.
        expected_start: expectedStart,
        expected_end: expectedEnd,
        // confirm=true : l'admin a confirmé (2 étapes pour la réouverture).
        // Toujours transmis → robuste si une édition concurrente transforme
        // une extension en réduction côté serveur (il exigera confirm).
        confirm: true,
      };
      const trimmed = (reason || "").trim();
      if (trimmed) body.reason = trimmed.slice(0, 200);

      const res = await fetch(
        "/api/beds24-mapping?action=pricing_block_edit_dates",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${session.access_token}`,
          },
          body: JSON.stringify(body),
        },
      );
      const json = await res.json().catch(() => ({}));
      // `ok:false` = échec, QUEL QUE SOIT le code HTTP. Depuis 2026-09-21 le
      // serveur rend un 502 franc quand une réouverture n'est pas passée (il
      // remet alors le blocage à ses dates d'avant) ; ce garde-fou reste comme
      // ceinture si un 200 portait un jour `ok:false`. Dans les deux cas c'est
      // la PHRASE DU SERVEUR qui s'affiche : elle nomme les nuits concernées,
      // l'ancien texte générique les perdait.
      const echecSous200 = res.ok && json?.ok === false && !json?.noop;
      if (!res.ok || echecSous200) {
        setServerError(errorMessageFor(res.status, json));
        setSubmitting(false);
        submittingRef.current = false;
        setPhase("edit");
        return;
      }
      setSuccessMsg(successMessageFor(json));
      setSubmitting(false);
      submittingRef.current = false;
      if (onSaved) onSaved(json);
    } catch (e) {
      // Jamais de catch muet.
      console.error("[BlockEditDatesModal] edit dates failed", e);
      setServerError(e?.message || "Erreur réseau — réessayez.");
      setSubmitting(false);
      submittingRef.current = false;
      setPhase("edit");
    }
  }

  const labelStyle = {
    display: "block",
    fontSize: 11,
    fontWeight: 700,
    color: DS.navy,
    marginBottom: 4,
    textTransform: "uppercase",
    letterSpacing: 0.4,
  };
  const inputStyle = {
    width: "100%",
    padding: "8px 10px",
    borderRadius: 6,
    border: `0.5px solid ${DS.borderHover}`,
    fontSize: 13,
    fontFamily: "inherit",
    boxSizing: "border-box",
  };

  return (
    <div
      onClick={() => {
        if (!submitting) onClose();
      }}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,.5)",
        zIndex: 1100,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 16,
        fontFamily: DS.fontBody,
      }}
    >
      <div
        className="fbz-modal-sheet"
        data-testid="block-edit-dates-modal"
        onClick={(e) => e.stopPropagation()}
        style={{
          background: DS.card,
          borderRadius: DS.radiusCard,
          maxWidth: 480,
          width: "100%",
          padding: 22,
          maxHeight: "90vh",
          overflowY: "auto",
        }}
      >
        {/* En-tête */}
        <div style={{ marginBottom: 14 }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              fontFamily: DS.fontDisplay,
              fontSize: 18,
              fontWeight: 600,
              color: DS.marineDark,
            }}
          >
            <CalendarClock size={18} strokeWidth={2.2} /> Modifier les dates du
            blocage
          </div>
          <div
            style={{
              fontSize: 13,
              color: DS.marineDark,
              marginTop: 8,
              fontWeight: 600,
            }}
          >
            {logementLabel || ""} · {formatDateFR(expectedStart)} →{" "}
            {formatDateFR(expectedEnd)}
          </div>
          <div
            style={{
              fontSize: 12,
              color: DS.textSecondary,
              marginTop: 6,
              lineHeight: 1.5,
            }}
          >
            Raccourcir remet des nuits en vente sur Airbnb/Booking ; étendre les
            ferme. La fin est le jour de départ (exclu).
          </div>
        </div>

        {/* Succès */}
        {successMsg && (
          <div
            data-testid="block-edit-dates-success"
            style={{
              display: "flex",
              gap: 8,
              alignItems: "flex-start",
              padding: 10,
              background: DS.successBg,
              color: DS.successText,
              border: `1px solid ${DS.success}55`,
              borderRadius: 8,
              fontSize: 12,
              marginBottom: 12,
            }}
          >
            <Check
              size={14}
              strokeWidth={2.4}
              style={{ flexShrink: 0, marginTop: 1 }}
            />
            <span>{successMsg}</span>
          </div>
        )}

        {/* Erreur serveur */}
        {serverError && (
          <div
            data-testid="block-edit-dates-error"
            style={{
              display: "flex",
              gap: 8,
              alignItems: "flex-start",
              padding: 10,
              background: DS.dangerBg,
              color: DS.dangerText,
              border: `1px solid ${DS.danger}44`,
              borderRadius: 8,
              fontSize: 12,
              marginBottom: 12,
            }}
          >
            <AlertTriangle
              size={14}
              strokeWidth={2.2}
              style={{ flexShrink: 0, marginTop: 1 }}
            />
            <span>{serverError}</span>
          </div>
        )}

        {successMsg ? (
          // État terminal : un seul bouton Fermer (la fiche + le calendrier ont
          // déjà été rafraîchis par le parent via onSaved).
          <div style={{ display: "flex", justifyContent: "flex-end" }}>
            <button
              type="button"
              className="fbz-press"
              onClick={onClose}
              style={{
                background: DS.marine,
                color: "#fff",
                border: "none",
                borderRadius: DS.radiusControl,
                padding: "8px 16px",
                fontSize: 12,
                fontWeight: 700,
                fontFamily: "inherit",
                cursor: "pointer",
              }}
            >
              Fermer
            </button>
          </div>
        ) : phase === "confirmReopen" ? (
          // 2e étape : avertissement ROUGE avant réouverture réelle (chemin argent).
          <div>
            <div
              data-testid="block-edit-dates-reopen-warning"
              style={{
                display: "flex",
                gap: 8,
                alignItems: "flex-start",
                padding: 12,
                background: DS.dangerBg,
                color: DS.dangerText,
                border: `1px solid ${DS.danger}66`,
                borderRadius: 8,
                fontSize: 12,
                lineHeight: 1.5,
                marginBottom: 14,
              }}
            >
              <AlertTriangle
                size={16}
                strokeWidth={2.4}
                style={{ flexShrink: 0, marginTop: 1 }}
              />
              <span>
                <strong>
                  {reopenNights} nuit{reopenNights > 1 ? "s" : ""}{" "}
                  {reopenNights > 1 ? "seront" : "sera"} REMISE
                  {reopenNights > 1 ? "S" : ""} EN VENTE
                </strong>{" "}
                sur Airbnb/Booking ({describeZones(diff?.reopenZones)}). Une
                réservation peut arriver immédiatement sur ces nuits.
                {closeNights > 0 && (
                  <>
                    {" "}
                    {closeNights} autre{closeNights > 1 ? "s" : ""} nuit
                    {closeNights > 1 ? "s" : ""}{" "}
                    {closeNights > 1 ? "seront" : "sera"} fermée
                    {closeNights > 1 ? "s" : ""}.
                  </>
                )}
              </span>
            </div>
            <div
              style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}
            >
              <button
                type="button"
                className="fbz-press"
                onClick={() => setPhase("edit")}
                disabled={submitting}
                style={{
                  background: DS.card,
                  color: DS.marine,
                  border: `0.5px solid ${DS.borderHover}`,
                  borderRadius: DS.radiusControl,
                  padding: "8px 16px",
                  fontSize: 12,
                  fontWeight: 600,
                  fontFamily: "inherit",
                  cursor: submitting ? "not-allowed" : "pointer",
                }}
              >
                Retour
              </button>
              <button
                type="button"
                className="fbz-press"
                data-testid="block-edit-dates-confirm-reopen"
                onClick={submit}
                disabled={submitting}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  background: DS.danger,
                  color: "#fff",
                  border: "none",
                  borderRadius: DS.radiusControl,
                  padding: "8px 16px",
                  fontSize: 12,
                  fontWeight: 700,
                  fontFamily: "inherit",
                  cursor: submitting ? "not-allowed" : "pointer",
                  opacity: submitting ? 0.6 : 1,
                }}
              >
                {submitting ? (
                  <>
                    <Loader2 size={13} /> Réouverture…
                  </>
                ) : (
                  `Confirmer la réouverture de ${reopenNights} nuit${reopenNights > 1 ? "s" : ""}`
                )}
              </button>
            </div>
          </div>
        ) : (
          // 1re étape : saisie des dates + récap.
          <div>
            <div
              style={{
                display: "flex",
                gap: 12,
                marginBottom: 12,
                flexWrap: "wrap",
              }}
            >
              <div style={{ flex: "1 1 140px" }}>
                <label htmlFor="block-edit-start" style={labelStyle}>
                  Début (arrivée)
                </label>
                <input
                  id="block-edit-start"
                  type="date"
                  data-testid="block-edit-dates-start"
                  value={newStart}
                  min={today}
                  disabled={submitting || started}
                  onChange={(e) => setNewStart(e.target.value)}
                  style={{
                    ...inputStyle,
                    background: started ? "#f1f5f9" : DS.card,
                    cursor: started ? "not-allowed" : "text",
                  }}
                />
              </div>
              <div style={{ flex: "1 1 140px" }}>
                <label htmlFor="block-edit-end" style={labelStyle}>
                  Fin (départ, exclu)
                </label>
                <input
                  id="block-edit-end"
                  type="date"
                  data-testid="block-edit-dates-end"
                  value={newEnd}
                  min={newStart || today}
                  disabled={submitting}
                  onChange={(e) => setNewEnd(e.target.value)}
                  style={inputStyle}
                />
              </div>
            </div>

            {started && (
              <div
                style={{
                  fontSize: 11,
                  color: DS.textSecondary,
                  marginBottom: 12,
                  lineHeight: 1.5,
                }}
              >
                Ce blocage a déjà commencé : seule la date de fin est
                modifiable.
              </div>
            )}

            {/* Récap des nuits impactées */}
            <div
              data-testid="block-edit-dates-recap"
              style={{ marginBottom: 14 }}
            >
              {datesInvalid ? (
                <div style={{ fontSize: 12, color: DS.textSecondary }}>
                  Saisissez une période valide (la fin doit être après le
                  début).
                </div>
              ) : noChange ? (
                <div style={{ fontSize: 12, color: DS.textSecondary }}>
                  Aucun changement de dates.
                </div>
              ) : (
                <div
                  style={{ display: "flex", flexDirection: "column", gap: 8 }}
                >
                  {reopenNights > 0 && (
                    <div
                      data-testid="block-edit-dates-recap-reopen"
                      style={{
                        display: "flex",
                        gap: 8,
                        alignItems: "flex-start",
                        padding: 10,
                        background: DS.dangerBg,
                        color: DS.dangerText,
                        border: `1px solid ${DS.danger}55`,
                        borderRadius: 8,
                        fontSize: 12,
                        lineHeight: 1.5,
                      }}
                    >
                      <AlertTriangle
                        size={14}
                        strokeWidth={2.4}
                        style={{ flexShrink: 0, marginTop: 1 }}
                      />
                      <span>
                        <strong>
                          {reopenNights} nuit{reopenNights > 1 ? "s" : ""}{" "}
                          {reopenNights > 1 ? "seront" : "sera"} ROUVERTE
                          {reopenNights > 1 ? "S" : ""} à la vente
                        </strong>{" "}
                        sur Airbnb/Booking ({describeZones(diff.reopenZones)}).
                      </span>
                    </div>
                  )}
                  {closeNights > 0 && (
                    <div
                      data-testid="block-edit-dates-recap-close"
                      style={{
                        display: "flex",
                        gap: 8,
                        alignItems: "flex-start",
                        padding: 10,
                        background: DS.successBg,
                        color: DS.successText,
                        border: `1px solid ${DS.success}44`,
                        borderRadius: 8,
                        fontSize: 12,
                        lineHeight: 1.5,
                      }}
                    >
                      <Check
                        size={14}
                        strokeWidth={2.4}
                        style={{ flexShrink: 0, marginTop: 1 }}
                      />
                      <span>
                        {closeNights} nuit{closeNights > 1 ? "s" : ""}{" "}
                        {closeNights > 1 ? "seront" : "sera"} fermée
                        {closeNights > 1 ? "s" : ""} à la vente (
                        {describeZones(diff.closeZones)}).
                      </span>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div style={{ marginBottom: 14 }}>
              <label htmlFor="block-edit-reason" style={labelStyle}>
                Raison (facultatif)
              </label>
              <input
                id="block-edit-reason"
                type="text"
                data-testid="block-edit-dates-reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                disabled={submitting}
                placeholder="ex : séjour raccourci"
                maxLength={200}
                style={inputStyle}
              />
            </div>

            <div
              style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}
            >
              <button
                type="button"
                className="fbz-press"
                onClick={onClose}
                disabled={submitting}
                style={{
                  background: DS.card,
                  color: DS.marine,
                  border: `0.5px solid ${DS.borderHover}`,
                  borderRadius: DS.radiusControl,
                  padding: "8px 16px",
                  fontSize: 12,
                  fontWeight: 600,
                  fontFamily: "inherit",
                  cursor: submitting ? "not-allowed" : "pointer",
                }}
              >
                Annuler
              </button>
              {isReopen ? (
                // Raccourcissement (chemin argent) → 2e étape ROUGE obligatoire.
                <button
                  type="button"
                  className="fbz-press"
                  data-testid="block-edit-dates-continue"
                  onClick={() => {
                    setServerError(null);
                    setPhase("confirmReopen");
                  }}
                  disabled={submitting || datesInvalid || noChange}
                  style={{
                    background: "transparent",
                    color: DS.dangerText,
                    border: `1px solid ${DS.danger}66`,
                    borderRadius: DS.radiusControl,
                    padding: "8px 16px",
                    fontSize: 12,
                    fontWeight: 700,
                    fontFamily: "inherit",
                    cursor:
                      submitting || datesInvalid || noChange
                        ? "not-allowed"
                        : "pointer",
                    opacity: submitting || datesInvalid || noChange ? 0.5 : 1,
                  }}
                >
                  Continuer
                </button>
              ) : (
                // Extension seule (fermeture) → application directe.
                <button
                  type="button"
                  className="fbz-press"
                  data-testid="block-edit-dates-apply"
                  onClick={submit}
                  disabled={submitting || datesInvalid || noChange}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6,
                    background: DS.marine,
                    color: "#fff",
                    border: "none",
                    borderRadius: DS.radiusControl,
                    padding: "8px 16px",
                    fontSize: 12,
                    fontWeight: 700,
                    fontFamily: "inherit",
                    cursor:
                      submitting || datesInvalid || noChange
                        ? "not-allowed"
                        : "pointer",
                    opacity: submitting || datesInvalid || noChange ? 0.5 : 1,
                  }}
                >
                  {submitting ? (
                    <>
                      <Loader2 size={13} /> Enregistrement…
                    </>
                  ) : (
                    "Appliquer les nouvelles dates"
                  )}
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
